/**
 * LA RENTABILITÉ — lot 102, 18/09/2026.
 *
 * Maquettes `maquette-analyse-rentabilite-v2.html` et `-v3.html`, cadrage
 * « Analyse financière : 6. la rentabilité » et « 8. les arbitrages ».
 *
 * Par camion et par trajet : ce que le Résultat ne dit pas. Le coût RÉEL de la
 * comptabilité est réparti par des clés nommées à l'écran ; le point mort est
 * celui de la flotte seule.
 */

import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { POSTES_REELS, type LigneRenta, type Maille } from '@stafa/core';
import type { EchecApi } from './api.js';
import {
  enregistrerObjectif,
  enregistrerSalaire,
  lireRentabilite,
} from './apiAnalyse.js';
import { euros, eurosCourts } from './bfr.js';
import { CourbeAnalyse } from './CourbeAnalyse.js';
import { moisCourt, moisLong } from './sig.js';
import { eurosEntiers, eurosSignes } from './tresorerie.js';
import {
  cumulerRentabilite,
  euroKm,
  km,
  NOMS_MAILLES,
  objectifDuVehicule,
  pourcentage,
  trajetsDe,
  type RentabiliteLue,
} from './rentabilite.js';

export interface ActionsRentabilite {
  readonly lireMois: (mois: string) => Promise<RentabiliteLue | string>;
  readonly poserSalaire: (
    chauffeurId: string,
    mois: string,
    valeur: string | null,
  ) => Promise<string | null>;
  readonly poserObjectif: (
    immatriculation: string,
    mois: string,
    valeur: string | null,
  ) => Promise<string | null>;
}

/** Les douze derniers mois, du plus récent au plus ancien. */
function douzeMois(aujourdhui: string): string[] {
  const a = Number(aujourdhui.slice(0, 4));
  const m = Number(aujourdhui.slice(5, 7));
  const res: string[] = [];
  for (let k = 0; k < 12; k += 1) {
    res.push(new Date(Date.UTC(a, m - 1 - k, 1)).toISOString().slice(0, 7));
  }
  return res;
}

export function EcranRentabilite({
  jeton,
  quandAccesPerdu,
  messageRefus,
  sources,
}: {
  readonly jeton: () => Promise<string | null>;
  readonly quandAccesPerdu: () => void;
  readonly messageRefus: (cause: EchecApi) => string;
  readonly sources: (quandLu: () => void) => ReactNode;
}) {
  const [donnees, setDonnees] = useState<RentabiliteLue | null>(null);
  const [refus, setRefus] = useState<string | null>(null);
  const [mois, setMois] = useState<string | null>(null);

  async function lire(quel: string | null): Promise<RentabiliteLue | string> {
    const j = await jeton();
    if (j === null) {
      quandAccesPerdu();
      return 'Session terminée.';
    }
    const r = await lireRentabilite(j, quel);
    if (r.etat !== 'ok') {
      if (r.cause === 'acces') quandAccesPerdu();
      return r.message ?? messageRefus(r.cause);
    }
    return r.valeur;
  }

  async function charger(quel: string | null) {
    const r = await lire(quel);
    if (typeof r === 'string') setRefus(r);
    else {
      setDonnees(r);
      setMois(r.mois);
      setRefus(null);
    }
  }

  useEffect(() => {
    void charger(null);
  }, [jeton]);

  const actions: ActionsRentabilite = {
    lireMois: lire,
    poserSalaire: async (chauffeurId, m, valeur) => {
      const j = await jeton();
      if (j === null) {
        quandAccesPerdu();
        return 'Session terminée.';
      }
      const r = await enregistrerSalaire(j, chauffeurId, m, valeur);
      if (r.etat !== 'ok') {
        if (r.cause === 'acces') quandAccesPerdu();
        return r.message ?? messageRefus(r.cause);
      }
      await charger(mois);
      return null;
    },
    poserObjectif: async (immatriculation, m, valeur) => {
      const j = await jeton();
      if (j === null) {
        quandAccesPerdu();
        return 'Session terminée.';
      }
      const r = await enregistrerObjectif(j, {
        mois: m,
        vue: 'vehicule',
        cible: immatriculation,
        indicateur: 'marge_km',
        valeur,
      });
      if (r.etat !== 'ok') {
        if (r.cause === 'acces') quandAccesPerdu();
        return r.message ?? messageRefus(r.cause);
      }
      await charger(mois);
      return null;
    },
  };

  return (
    <VueRentabilite
      donnees={donnees}
      refus={refus}
      sources={sources(() => void charger(mois))}
      actions={actions}
      quandMois={(m) => void charger(m)}
    />
  );
}

type Panneau = 'sources' | 'salaires' | 'objectifs' | null;

export function VueRentabilite({
  donnees,
  refus,
  sources,
  actions,
  quandMois,
  panneauInitial = null,
  detailInitial = null,
}: {
  readonly donnees: RentabiliteLue | null;
  readonly refus: string | null;
  readonly sources: ReactNode;
  readonly actions: ActionsRentabilite;
  readonly quandMois: (mois: string) => void;
  readonly panneauInitial?: Panneau;
  readonly detailInitial?: string | null;
}) {
  const [panneau, setPanneau] = useState<Panneau>(panneauInitial);
  const [detail, setDetail] = useState<string | null>(detailInitial);
  const [maille, setMaille] = useState<Maille>('departement');
  const [cumul, setCumul] = useState<RentabiliteLue | null>(null);
  const [chargement, setChargement] = useState<{ fait: number; total: number } | null>(
    null,
  );
  const [message, setMessage] = useState<string | null>(null);

  const vue = cumul ?? donnees;
  const trajets = useMemo(
    () => (vue === null ? [] : trajetsDe(vue, maille)),
    [vue, maille],
  );

  if (donnees === null || vue === null) {
    return (
      <main className="corps corps-analyse">
        {refus !== null ? (
          <p className="bandeau bandeau-refus">{refus}</p>
        ) : (
          <p className="attente">Lecture des journées et de la comptabilité…</p>
        )}
      </main>
    );
  }

  async function chargerLeCumul() {
    const liste = douzeMois(donnees?.aujourdhui ?? '').reverse();
    const lus: RentabiliteLue[] = [];
    setChargement({ fait: 0, total: liste.length });
    for (const [i, m] of liste.entries()) {
      const r = await actions.lireMois(m);
      if (typeof r === 'string') {
        setChargement(null);
        setMessage(r);
        return;
      }
      lus.push(r);
      setChargement({ fait: i + 1, total: liste.length });
    }
    setChargement(null);
    setCumul(cumulerRentabilite(lus));
  }

  const t = vue.totaux;
  const margeKm = t.km > 0 ? Math.round(t.marge / t.km) : null;
  const margePrevue = t.ca - t.cout_prevu;

  return (
    <main className="corps corps-analyse">
      <div className="bfr-filtres">
        <label className="ech-champ">
          Mois
          <select
            value={cumul === null ? donnees.mois : ''}
            onChange={(e) => {
              setCumul(null);
              quandMois(e.target.value);
            }}
          >
            {cumul !== null && <option value="">Cumul 12 mois</option>}
            {douzeMois(donnees.aujourdhui).map((m) => (
              <option key={m} value={m}>
                {moisLong(m)}
              </option>
            ))}
          </select>
        </label>
        <div className="bfr-outils">
          <button
            type="button"
            className="bouton bouton-petit"
            disabled={chargement !== null}
            onClick={() => (cumul === null ? void chargerLeCumul() : setCumul(null))}
          >
            {chargement !== null
              ? `Cumul ${String(chargement.fait)}/${String(chargement.total)}…`
              : cumul === null
                ? 'Cumul 12 mois'
                : 'Revenir au mois'}
          </button>
          <button
            type="button"
            className="bouton bouton-discret bouton-petit"
            aria-expanded={panneau === 'salaires'}
            onClick={() => setPanneau(panneau === 'salaires' ? null : 'salaires')}
          >
            Salaires des chauffeurs
          </button>
          <button
            type="button"
            className="bouton bouton-discret bouton-petit"
            aria-expanded={panneau === 'objectifs'}
            onClick={() => setPanneau(panneau === 'objectifs' ? null : 'objectifs')}
          >
            Objectifs de marge au km
          </button>
          <button
            type="button"
            className="bouton bouton-discret bouton-petit"
            aria-expanded={panneau === 'sources'}
            onClick={() => setPanneau(panneau === 'sources' ? null : 'sources')}
          >
            Données Pennylane
          </button>
        </div>
      </div>
      <p className="ech-sous">
        {cumul === null ? moisLong(donnees.mois) : `Cumul : ${vue.mois}`} · du {vue.du}{' '}
        au {vue.au}
        {' · '}Coût réel réparti depuis la balance des comptes ; salaires depuis les
        saisies.
        {vue.salaires_estimes &&
          ' · Des salaires sont ESTIMÉS (prix de revient) faute de saisie.'}
        {vue.sans_balance && ' · Balance non lue pour ce mois : le coût réel vaut 0.'}
        {vue.surcharge_non_comptee &&
          ' · La surcharge gasoil n’est pas comptée dans le chiffre d’affaires.'}
      </p>
      {message !== null && <p className="bandeau bandeau-refus">{message}</p>}
      {refus !== null && <p className="bandeau bandeau-refus">{refus}</p>}
      {vue.incalculables.length > 0 && (
        <p className="bandeau bandeau-attente">
          {vue.incalculables.length} journée(s) hors du calcul :{' '}
          {vue.incalculables[0]?.pourquoi}
        </p>
      )}
      {panneau === 'sources' && <div className="ech-sources">{sources}</div>}
      {panneau === 'salaires' && (
        <div className="ech-sources">
          <PanneauSalaires donnees={donnees} actions={actions} />
        </div>
      )}
      {panneau === 'objectifs' && (
        <div className="ech-sources">
          <PanneauObjectifsVehicule donnees={donnees} actions={actions} />
        </div>
      )}

      <div className="treso-tuiles">
        <section className="treso-tuile">
          <h3>Chiffre d’affaires de la flotte</h3>
          <strong>{eurosEntiers(t.ca)}</strong>
          <p>
            {km(t.km)} · {String(vue.lignes.filter((l) => l.vehicule.jours > 0).length)}{' '}
            camion(s) en service
          </p>
        </section>
        <section
          className={`treso-tuile ${t.marge >= 0 ? 'treso-vert' : 'treso-rouge'}`}
        >
          <h3>Marge réelle</h3>
          <strong>{eurosEntiers(t.marge)}</strong>
          <p>
            prévue {eurosEntiers(margePrevue)} · écart{' '}
            {eurosSignes(t.marge - margePrevue)}
          </p>
        </section>
        <section className="treso-tuile">
          <h3>Marge réelle au km</h3>
          <strong>{euroKm(margeKm)}</strong>
          <p>coût réel {euroKm(t.km > 0 ? Math.round(t.cout_reel / t.km) : null)}</p>
        </section>
        <section className="treso-tuile">
          <h3>Kilomètres à vide</h3>
          <strong>{pourcentage(t.km > 0 ? t.km_vide / t.km : null)}</strong>
          <p>{km(t.km_vide)} sans rien à bord</p>
        </section>
      </div>

      {cumul === null && (
        <section className="bfr-section">
          <h2 className="bfr-section-titre">Point mort du mois (flotte seule)</h2>
          <p className="bfr-calcul">
            Fixes {eurosEntiers(vue.point_mort.fixes)} ÷ taux de marge sur coûts
            variables {pourcentage(vue.point_mort.taux)} ={' '}
            <strong>
              {vue.point_mort.seuil === null ? '—' : eurosEntiers(vue.point_mort.seuil)}
            </strong>
            {' · '}
            {vue.point_mort.atteint_le === null
              ? 'pas encore atteint'
              : `atteint le ${vue.point_mort.atteint_le}`}
            {vue.point_mort.projection !== null &&
              ` · projection du mois entier : ${eurosEntiers(vue.point_mort.projection)}`}
          </p>
          {vue.point_mort.cumul.length > 0 && (
            <CourbeAnalyse
              titre="Chiffre d’affaires cumulé"
              sousTitre="jour par jour, face au point mort"
              etiquettes={vue.point_mort.cumul.map((c) => c.jour.slice(8, 10))}
              sousEtiquettes={vue.point_mort.cumul.map((c) => c.jour.slice(5, 7))}
              series={[
                {
                  nom: 'CA cumulé',
                  teinte: 1,
                  valeurs: vue.point_mort.cumul.map((c) => c.ca),
                },
              ]}
              seuils={
                vue.point_mort.seuil === null
                  ? []
                  : [{ valeur: vue.point_mort.seuil, nom: 'point mort' }]
              }
              courant={-1}
              futurDepuis={vue.point_mort.cumul.length}
              format={eurosCourts}
            />
          )}
        </section>
      )}

      <section className="bfr-section">
        <h2 className="bfr-section-titre">Par camion</h2>
        <div className="treso-cadre">
          <table className="treso-tableau">
            <thead>
              <tr>
                <th scope="col">Camion</th>
                <th scope="col" className="nombre">
                  Jours
                </th>
                <th scope="col" className="nombre">
                  Km
                </th>
                <th scope="col" className="nombre">
                  À vide
                </th>
                <th scope="col" className="nombre">
                  CA
                </th>
                <th scope="col" className="nombre">
                  CA/km
                </th>
                <th scope="col" className="nombre">
                  CA/jour
                </th>
                <th scope="col" className="nombre">
                  Coût prévu
                </th>
                <th scope="col" className="nombre">
                  Coût réel
                </th>
                <th scope="col" className="nombre">
                  Écart
                </th>
                <th scope="col" className="nombre">
                  Marge
                </th>
                <th scope="col" className="nombre">
                  Taux
                </th>
                <th scope="col" className="nombre">
                  Marge/km
                </th>
                <th scope="col" className="nombre">
                  Objectif
                </th>
              </tr>
            </thead>
            <tbody>
              {vue.lignes.map((l) => {
                const objectif = objectifDuVehicule(vue, l.vehicule.immatriculation);
                const sous =
                  objectif !== null && l.marge_km !== null && l.marge_km < objectif;
                return (
                  <tr key={l.vehicule.id}>
                    <th scope="row">
                      <button
                        type="button"
                        className="treso-case"
                        onClick={() => setDetail(l.vehicule.id)}
                      >
                        {l.vehicule.immatriculation}
                      </button>
                      <span className="ech-petit ech-doux">
                        {' '}
                        {l.vehicule.type ?? ''}
                      </span>
                    </th>
                    <td className="nombre">{l.vehicule.jours}</td>
                    <td className="nombre">{l.vehicule.km.toLocaleString('fr-FR')}</td>
                    <td className="nombre">{pourcentage(l.part_vide, 0)}</td>
                    <td className="nombre">{eurosEntiers(l.vehicule.ca)}</td>
                    <td className="nombre">{euroKm(l.ca_km)}</td>
                    <td className="nombre">{eurosEntiers(l.ca_jour ?? 0)}</td>
                    <td className="nombre">{eurosEntiers(l.vehicule.cout_prevu)}</td>
                    <td className="nombre">{eurosEntiers(l.cout_reel)}</td>
                    <td className="nombre">{eurosSignes(l.ecart)}</td>
                    <td className={`nombre ${l.marge < 0 ? 'renta-perte' : ''}`}>
                      {eurosEntiers(l.marge)}
                    </td>
                    <td className="nombre">{pourcentage(l.taux)}</td>
                    <td className={`nombre ${sous ? 'renta-perte' : ''}`}>
                      {euroKm(l.marge_km)}
                    </td>
                    <td className="nombre">
                      {objectif === null ? '—' : euroKm(objectif)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      <section className="bfr-section">
        <h2 className="bfr-section-titre">Par trajet</h2>
        <div className="bfr-filtres">
          <div className="ech-champ">
            Maille
            <div className="bfr-segments" role="group" aria-label="Maille">
              {NOMS_MAILLES.map((m) => (
                <button
                  key={m.cle}
                  type="button"
                  aria-pressed={maille === m.cle}
                  onClick={() => setMaille(m.cle)}
                >
                  {m.nom}
                </button>
              ))}
            </div>
          </div>
          <p className="ech-petit ech-doux">
            La région n’est pas servie : Stafa ne sait pas dans quelle région est un
            site.
          </p>
        </div>
        <div className="treso-cadre">
          <table className="treso-tableau">
            <thead>
              <tr>
                <th scope="col">Trajet</th>
                <th scope="col" className="nombre">
                  Transports
                </th>
                <th scope="col" className="nombre">
                  Km
                </th>
                <th scope="col" className="nombre">
                  CA
                </th>
                <th scope="col" className="nombre">
                  Coût
                </th>
                <th scope="col" className="nombre">
                  Marge
                </th>
                <th scope="col" className="nombre">
                  Taux
                </th>
              </tr>
            </thead>
            <tbody>
              {trajets.length === 0 ? (
                <tr>
                  <td colSpan={7}>Aucun transport sur la période.</td>
                </tr>
              ) : (
                trajets.map((x) => (
                  <tr key={x.cle}>
                    <th scope="row">
                      {x.cle}
                      {x.peu && <span className="ech-etiquette">moins de 3</span>}
                    </th>
                    <td className="nombre">{x.transports.length}</td>
                    <td className="nombre">{x.km.toLocaleString('fr-FR')}</td>
                    <td className="nombre">{eurosEntiers(x.ca)}</td>
                    <td className="nombre">{eurosEntiers(x.cout)}</td>
                    <td className={`nombre ${x.marge < 0 ? 'renta-perte' : ''}`}>
                      {eurosEntiers(x.marge)}
                    </td>
                    <td className="nombre">{pourcentage(x.taux)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      {detail !== null && (
        <FenetreCamion
          ligne={vue.lignes.find((l) => l.vehicule.id === detail) ?? null}
          donnees={vue}
          quandFermer={() => setDetail(null)}
        />
      )}
    </main>
  );
}

// ---------------------------------------------------------------------------
// Les panneaux
// ---------------------------------------------------------------------------

export function PanneauSalaires({
  donnees,
  actions,
}: {
  readonly donnees: RentabiliteLue;
  readonly actions: ActionsRentabilite;
}) {
  const [message, setMessage] = useState<string | null>(null);
  return (
    <section className="reglages-analyse" aria-label="Salaires des chauffeurs">
      <h3>Coût employeur des conducteurs · {moisLong(donnees.mois)}</h3>
      <p className="ech-sous">
        Stafa n’a pas la paie : ce montant est saisi ici, mois par mois. Vide : le
        salaire prévu du prix de revient est pris, et la ligne est marquée « estimé ».
        Il se répartit sur les camions au prorata des jours d’attelage.
      </p>
      <div className="treso-cadre">
        <table className="treso-tableau">
          <thead>
            <tr>
              <th scope="col">Conducteur</th>
              <th scope="col" className="nombre">
                Jours d’attelage
              </th>
              <th scope="col" className="nombre">
                Coût employeur
              </th>
              <th scope="col">Camions</th>
            </tr>
          </thead>
          <tbody>
            {donnees.salaires.map((s) => {
              const siens = donnees.attelages.filter(
                (a) => a.chauffeur_id === s.chauffeur_id,
              );
              return (
                <tr key={s.chauffeur_id}>
                  <th scope="row">
                    {s.nom}
                    {s.estime && <span className="ech-etiquette">estimé</span>}
                  </th>
                  <td className="nombre">{s.jours}</td>
                  <td className="nombre">
                    <ChampMontant
                      valeur={s.estime ? null : s.cout}
                      aria="coût employeur"
                      quandPoser={(v) =>
                        actions
                          .poserSalaire(s.chauffeur_id, donnees.mois, v)
                          .then(setMessage)
                      }
                    />
                  </td>
                  <td className="ech-petit ech-doux">
                    {siens.length === 0
                      ? 'aucun attelage ce mois-ci'
                      : siens
                          .map(
                            (a) =>
                              `${donnees.lignes.find((l) => l.vehicule.id === a.vehicule_id)?.vehicule.immatriculation ?? a.vehicule_id} (${String(a.jours)} j)`,
                          )
                          .join(' · ')}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {message !== null && <p className="ech-ko">{message}</p>}
    </section>
  );
}

export function PanneauObjectifsVehicule({
  donnees,
  actions,
}: {
  readonly donnees: RentabiliteLue;
  readonly actions: ActionsRentabilite;
}) {
  const [message, setMessage] = useState<string | null>(null);
  return (
    <section className="reglages-analyse" aria-label="Objectifs de marge au km">
      <h3>Objectif de marge au kilomètre · {moisLong(donnees.mois)}</h3>
      <p className="ech-sous">
        En euros par kilomètre. Vide : pas d’objectif — rien n’est proposé.
      </p>
      <div className="treso-cadre">
        <table className="treso-tableau">
          <thead>
            <tr>
              <th scope="col">Camion</th>
              <th scope="col" className="nombre">
                Marge au km
              </th>
              <th scope="col" className="nombre">
                Objectif
              </th>
            </tr>
          </thead>
          <tbody>
            {donnees.lignes.map((l) => (
              <tr key={l.vehicule.id}>
                <th scope="row">{l.vehicule.immatriculation}</th>
                <td className="nombre">{euroKm(l.marge_km)}</td>
                <td className="nombre">
                  <ChampMontant
                    valeur={objectifDuVehicule(donnees, l.vehicule.immatriculation)}
                    aria="objectif"
                    quandPoser={(v) =>
                      actions
                        .poserObjectif(l.vehicule.immatriculation, donnees.mois, v)
                        .then(setMessage)
                    }
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {message !== null && <p className="ech-ko">{message}</p>}
    </section>
  );
}

function ChampMontant({
  valeur,
  aria,
  quandPoser,
}: {
  readonly valeur: number | null;
  readonly aria: string;
  readonly quandPoser: (v: string | null) => Promise<void>;
}) {
  const ecrit = valeur === null ? '' : String(valeur / 100).replace('.', ',');
  const [saisie, setSaisie] = useState(ecrit);
  useEffect(() => setSaisie(ecrit), [ecrit]);
  return (
    <input
      className="sig-objectif"
      inputMode="decimal"
      aria-label={aria}
      value={saisie}
      onChange={(e) => setSaisie(e.target.value)}
      onBlur={() => {
        if (saisie === ecrit) return;
        void quandPoser(saisie.trim() === '' ? null : saisie.trim());
      }}
    />
  );
}

// ---------------------------------------------------------------------------
// Le détail d'un camion
// ---------------------------------------------------------------------------

function FenetreCamion({
  ligne,
  donnees,
  quandFermer,
}: {
  readonly ligne: LigneRenta | null;
  readonly donnees: RentabiliteLue;
  readonly quandFermer: () => void;
}) {
  useEffect(() => {
    const touche = (e: KeyboardEvent) => {
      if (e.key === 'Escape') quandFermer();
    };
    window.addEventListener('keydown', touche);
    return () => window.removeEventListener('keydown', touche);
  }, [quandFermer]);
  if (ligne === null) return null;
  const siens = donnees.trajets.filter((t) => t.vehicule_id === ligne.vehicule.id);

  return (
    <div className="ech-voile bfr-voile" role="presentation" onClick={quandFermer}>
      <div
        className="bfr-fenetre"
        role="dialog"
        aria-modal="true"
        aria-label={ligne.vehicule.immatriculation}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="ech-panneau-tete">
          <div>
            <h2>{ligne.vehicule.immatriculation}</h2>
            <p className="ech-sous">
              {ligne.vehicule.type ?? 'type inconnu'} · {String(ligne.vehicule.jours)}{' '}
              jour(s) · {km(ligne.vehicule.km)} dont {pourcentage(ligne.part_vide, 0)} à
              vide
            </p>
          </div>
          <button
            type="button"
            className="ech-fermer"
            aria-label="Fermer"
            onClick={quandFermer}
          >
            ×
          </button>
        </div>
        <p className="bfr-calcul">
          {eurosEntiers(ligne.vehicule.ca)} de CA − {eurosEntiers(ligne.cout_reel)} de
          coût réel = <strong>{eurosEntiers(ligne.marge)}</strong> · coût prévu{' '}
          {eurosEntiers(ligne.vehicule.cout_prevu)}
        </p>
        <div className="ech-tableau">
          <table>
            <thead>
              <tr>
                <th>Poste réel</th>
                <th>Clé de répartition</th>
                <th className="nombre">Montant</th>
              </tr>
            </thead>
            <tbody>
              {POSTES_REELS.map((p) => (
                <tr key={p.cle}>
                  <td>{p.nom}</td>
                  <td className="ech-petit ech-doux">{p.cledit}</td>
                  <td className="nombre">{euros(ligne.postes[p.cle])}</td>
                </tr>
              ))}
              <tr>
                <th scope="row">Total</th>
                <td />
                <td className="nombre">
                  <strong>{euros(ligne.cout_reel)}</strong>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <details className="bfr-bloc" open>
          <summary>
            <span>
              Transports du mois <span className="ech-doux">({siens.length})</span>
            </span>
            <strong>{euros(siens.reduce((a, t) => a + t.ca, 0))}</strong>
          </summary>
          {siens.length === 0 ? (
            <p className="ech-vide">
              Aucun transport rattaché à ce camion sur la période.
            </p>
          ) : (
            <div className="ech-tableau">
              <table>
                <thead>
                  <tr>
                    <th>Ordre</th>
                    <th>Client</th>
                    <th>Trajet</th>
                    <th className="nombre">Km</th>
                    <th className="nombre">CA</th>
                  </tr>
                </thead>
                <tbody>
                  {siens.map((x) => (
                    <tr key={x.ot_id}>
                      <td>{x.numero}</td>
                      <td>{x.client_nom}</td>
                      <td className="ech-petit">
                        {x.depart.ville} → {x.arrivee.ville}
                      </td>
                      <td className="nombre">{x.km.toLocaleString('fr-FR')}</td>
                      <td className="nombre">{euros(x.ca)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </details>
        <p className="ech-petit ech-doux">
          Mois observé : {moisCourt(donnees.mois)}. Le coût réel vient de la
          comptabilité, réparti par les clés ci-dessus — aucune facture de carburant ne
          porte le camion.
        </p>
      </div>
    </div>
  );
}
